import { FC, useMemo } from 'react';
import { Skeleton, styled, Typography } from '@mui/material';
import { currencyFormat } from 'common/formats/formats';
import { formatNumbers } from 'utilities';
import { EnterpriseValueProps } from './types';

// Blue gradient opinion banner from the Overview redesign: serif title,
// enterprise value, value per common share, and the expiry note, clustered
// centrally with a lighter decorative sweep on the right.
const Banner = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexWrap: 'wrap',
  columnGap: 64,
  rowGap: 16,
  padding: '32px 40px',
  borderRadius: 14,
  color: '#FFFFFF',
  background:
    'radial-gradient(70% 160% at 100% 0%, rgba(120, 180, 255, 0.5) 0%, transparent 55%), ' +
    'linear-gradient(90deg, #0A5BEB 0%, #1668F0 100%)',
});

const OpinionTitle = styled(Typography)({
  fontFamily: 'Georgia, "Times New Roman", serif',
  fontSize: '2rem',
  lineHeight: 1.2,
  color: 'inherit',
});

const Metric = styled('div')({
  textAlign: 'center',
});

const MetricLabel = styled(Typography)({
  fontSize: '0.875rem',
  marginTop: 6,
  opacity: 0.9,
  color: 'inherit',
});

const EnterpriseValueText = styled(Typography)({
  fontSize: '1.6rem',
  fontWeight: 600,
  color: 'inherit',
});

const ValuePerCommonText = styled(Typography)({
  fontSize: '2.5rem',
  fontWeight: 800,
  color: 'inherit',
});

const ExpiryNote = styled('div')<{ expired: boolean }>(({ expired }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  maxWidth: 140,
  color: expired ? '#FFB199' : '#00FFA3',
  fontWeight: 600,
  fontSize: '1rem',
  lineHeight: 1.3,
}));

const ValueSkeleton = styled(Skeleton)({
  backgroundColor: 'rgba(255, 255, 255, 0.35)',
  width: 120,
  margin: 'auto',
});

const EnterpriseValue: FC<EnterpriseValueProps> = ({ enterpriseData, isLoading, format, expiry }) => {
  const { enterpriseValue, valuePerCommon } = useMemo(() => {
    if (enterpriseData && !isLoading && format) {
      const {
        weighted_market_enterprise_value: enterpriseValueData,
        non_marketable_value_per_common_share: valuePerCommonData,
      } = enterpriseData;

      const { currency } = format;

      const enterpriseValueFormated = formatNumbers({
        format: currencyFormat({ fractionDigits: 0 }),
        value: enterpriseValueData,
        prefix: null,
        suffix: null,
        allowNegativeValue: true,
        currency,
        currencyCode: currency.code,
        blankIfNull: false,
        staticCurrency: null,
      });

      const valuePerCommonFormated = formatNumbers({
        format: currencyFormat({ fractionDigits: 2 }),
        value: valuePerCommonData,
        prefix: null,
        suffix: null,
        allowNegativeValue: true,
        currency,
        currencyCode: currency.code,
        blankIfNull: false,
        staticCurrency: null,
      });

      return { enterpriseValue: enterpriseValueFormated, valuePerCommon: valuePerCommonFormated };
    }

    return { enterpriseValue: null, valuePerCommon: null };
  }, [enterpriseData, isLoading, format]);

  return (
    <Banner>
      <OpinionTitle>
        Valuation
        <br />
        <b>Opinion</b>
      </OpinionTitle>
      <Metric>
        {!isLoading && enterpriseValue ? (
          <EnterpriseValueText>{enterpriseValue}</EnterpriseValueText>
        ) : (
          <ValueSkeleton variant="text" />
        )}
        <MetricLabel>Enterprise Value</MetricLabel>
      </Metric>
      <Metric>
        {!isLoading && valuePerCommon ? (
          <ValuePerCommonText>{valuePerCommon}</ValuePerCommonText>
        ) : (
          <ValueSkeleton variant="text" />
        )}
        <MetricLabel>Value per Common Share</MetricLabel>
      </Metric>
    </Banner>
  );
};

export default EnterpriseValue;
